/**
 * glend-attribution.js — First-party campaign attribution.
 *
 * WHY: In Spain the Shopify consent banner defaults to "denied", so a visitor
 * who ignores or declines it produces no Shopify customer journey and no Meta
 * web/CAPI event. Converting orders from that cohort arrive with
 * customerJourneySummary.firstVisit = null (e.g. order #1085, 2026-07-11).
 * This script captures the landing campaign as FIRST-PARTY data we attach to
 * our OWN cart/order, so attribution survives a consent-decline.
 *
 * WHAT IT DOES:
 *   1. On landing, reads utm_source/medium/campaign/content/term + fbclid/gclid
 *      + landing PATH (no query string) from the URL. When the URL carries no
 *      campaign signal at all, falls back to the HOST of document.referrer.
 *
 *      The fallback is why this covers more than one engine. ChatGPT appends
 *      ?utm_source=chatgpt.com, which is the only reason order #1091 was
 *      attributable; Perplexity, Claude, Copilot and Gemini generally append
 *      nothing and send an ordinary referrer instead. Without the fallback an
 *      order from any of those is captured as nothing at all: no UTM, no
 *      Shopify journey under a declined consent banner, no record anywhere.
 *
 *      The host is stored raw and NOT matched against a list of engines here.
 *      Naming engines is the dashboard's job (lab/venture/sync.toml,
 *      [[channel_rule]]), so a new assistant is a config edit rather than a
 *      theme deploy.
 *   2. Stores them first-touch in a first-party cookie `glend_attribution`
 *      (30 days). First touch wins — never overwritten by a later visit.
 *   3. Writes them to cart attributes (glend_first_*) via /cart/update.js, but
 *      ONLY once the cart actually has items (on the first successful
 *      /cart/add, or on load if a cart already exists) — never on a bare
 *      landing, to avoid creating phantom carts.
 *   4. Guards first-touch at the cart level too: if the cart already carries
 *      any glend_first_* attribute, it is left untouched.
 *
 * DATA MINIMISATION: campaign strings + click tokens + landing path + referrer
 * HOST only. Never the full referrer URL, which can carry a search query or a
 * document title. Self-referrals are dropped, so ordinary browsing inside the
 * store records nothing. No behavioural data, no third-party calls — this never
 * leaves our own order.
 *
 * PRIVACY POLICY (add, Castilian Spanish / tuteo):
 *   "Guardamos de forma temporal los datos de la campaña por la que llegas
 *    (origen, campaña y parámetros de la URL, y el dominio desde el que entras)
 *    en tu pedido, para saber qué contenido te resulta útil. No compartimos esta
 *    información con terceros."
 *
 * Do NOT add a custom Meta fbq AddToCart here — Shopify's native web pixel
 * already fires AddToCart per item; a manual call would double-count.
 */
(function () {
  'use strict';

  // Node has neither, and assets/glend-attribution.test.js loads this file to
  // exercise the pure parsers. Matches the guard in bakery-delivery.js.
  var IS_BROWSER = typeof window !== 'undefined' && typeof document !== 'undefined';
  if (IS_BROWSER) {
    if (window.Shopify && window.Shopify.designMode) return;
    if (window.__glendAttributionLoaded) return;
    window.__glendAttributionLoaded = true;
  }

  var COOKIE = 'glend_attribution';
  var COOKIE_DAYS = 30;

  // URL param -> cart attribute key
  var FIELDS = {
    utm_source: 'glend_first_utm_source',
    utm_medium: 'glend_first_utm_medium',
    utm_campaign: 'glend_first_utm_campaign',
    utm_content: 'glend_first_utm_content',
    utm_term: 'glend_first_utm_term',
    fbclid: 'glend_first_fbclid',
    gclid: 'glend_first_gclid'
  };
  var LANDING_KEY = 'glend_first_landing';
  var REFERRER_KEY = 'glend_first_referrer';

  // Hosts that mean "already ours", so a referrer from one of them is a
  // mid-journey artefact and not an acquisition source. location.hostname is
  // added at call time, which covers the storefront domain and any locale host.
  // shop.app is deliberately NOT here: the Shop app is a real acquisition
  // surface, and four orders have come through it.
  var SELF_HOSTS = ['admin.shopify.com', 'pay.shopify.com', 'checkout.shopify.com'];

  var writtenThisPage = false; // in-memory dedupe for the current page view

  function nfc(v) {
    try { return String(v).normalize('NFC'); } catch (e) { return String(v); }
  }

  function readCookie(name) {
    var m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[2]) : null;
  }

  function writeCookie(name, value) {
    var exp = new Date(Date.now() + COOKIE_DAYS * 864e5).toUTCString();
    var secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      name + '=' + encodeURIComponent(value) +
      '; expires=' + exp + '; path=/; SameSite=Lax' + secure;
  }

  // Campaign params out of a query string. Pure; returns an object or null.
  function parseParams(search) {
    var params;
    try { params = new URLSearchParams(search || ''); } catch (e) { return null; }
    var data = {};
    var found = false;
    Object.keys(FIELDS).forEach(function (p) {
      var val = params.get(p);
      if (val) { data[p] = nfc(val).slice(0, 500); found = true; }
    });
    return found ? data : null;   // no campaign signal on this landing
  }

  // host is `base`, or a subdomain of it.
  function isUnder(host, base) {
    if (!base) return false;
    if (host === base) return true;
    return host.slice(-(base.length + 1)) === '.' + base;
  }

  // Host of an external referrer, or null. Pure. Lowercased and stripped of a
  // leading www so 'www.google.com' and 'google.com' are one thing. Anything
  // that is us — the storefront itself, a subdomain of it, a myshopify host or
  // a Shopify checkout surface — is dropped: without this guard the FIRST
  // internal navigation of a cookieless visitor would record glend.shop as the
  // source, and every direct visit would look like a self-referral.
  function referrerSource(referrer, selfHost) {
    if (!referrer) return null;
    var url;
    try { url = new URL(referrer); } catch (e) { return null; }
    // Only the web. Android sends referrers like android-app://com.google.android.gm,
    // whose "hostname" is a package name and not a domain we could ever classify.
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    var host = String(url.hostname || '').toLowerCase().replace(/^www\./, '');
    if (!host) return null;
    if (isUnder(host, String(selfHost || '').toLowerCase().replace(/^www\./, ''))) return null;
    if (isUnder(host, 'myshopify.com')) return null;
    for (var i = 0; i < SELF_HOSTS.length; i++) {
      if (isUnder(host, SELF_HOSTS[i])) return null;
    }
    return nfc(host).slice(0, 253);   // a hostname cannot exceed 253 octets
  }

  // The whole first-touch decision, pure so it can be tested without a browser.
  // A campaign on the URL always outranks a referrer: it is the more specific
  // signal, and a Meta click carries both.
  function firstTouchFrom(search, referrer, selfHost, path) {
    var data = parseParams(search);
    if (!data) {
      var host = referrerSource(referrer, selfHost);
      if (!host) return null;
      data = { referrer: host };
    }
    data.landing = nfc(path || '/').slice(0, 500);   // path only, no query
    return data;
  }

  function parseUrl() {
    return firstTouchFrom(location.search, document.referrer, location.hostname,
                          location.pathname);
  }

  // Persist first-touch: only if we have no stored attribution yet.
  function captureFirstTouch() {
    var existing = readCookie(COOKIE);
    if (existing) {
      try { return JSON.parse(existing); } catch (e) { /* fall through */ }
    }
    var fresh = parseUrl();
    if (fresh) {
      writeCookie(COOKIE, JSON.stringify(fresh));
      return fresh;
    }
    return null;
  }

  // Build the cart-attribute payload from stored attribution.
  function toAttributes(data) {
    var attrs = {};
    Object.keys(FIELDS).forEach(function (p) {
      if (data[p]) attrs[FIELDS[p]] = nfc(data[p]);
    });
    if (data.referrer) attrs[REFERRER_KEY] = nfc(data.referrer);
    if (data.landing) attrs[LANDING_KEY] = nfc(data.landing);
    return attrs;
  }

  // True if the cart already carries any of our first-touch attributes.
  function alreadyAttributed(cart) {
    var a = (cart && cart.attributes) || {};
    if (a[LANDING_KEY] || a[REFERRER_KEY]) return true;
    var keys = Object.keys(FIELDS);
    for (var i = 0; i < keys.length; i++) {
      if (a[FIELDS[keys[i]]]) return true;
    }
    return false;
  }

  // Core: write attribution to the cart, respecting one-shot + phantom-cart
  // guards. Runs at most one /cart/update.js per page view.
  function writeIfNeeded(data) {
    if (writtenThisPage || !data) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || cart.item_count === 0) return;     // no phantom carts
        if (alreadyAttributed(cart)) { writtenThisPage = true; return; } // first-touch wins
        var attrs = toAttributes(data);
        if (!Object.keys(attrs).length) return;
        writtenThisPage = true;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        });
      })
      .catch(function () { writtenThisPage = false; }); // allow a later retry
  }

  // Intercept successful adds so we can attach attribution the moment a cart
  // exists. Matches /cart/add and /cart/add.js (incl. locale-prefixed paths).
  function installFetchHook(data) {
    if (window.__glendAttrFetchHooked) return;
    window.__glendAttrFetchHooked = true;
    var _fetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var res = _fetch.apply(this, args);
      try {
        var input = args[0];
        var url = typeof input === 'string' ? input
          : (input && (input.url || (input.toString && input.toString()))) || '';
        if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
          res.then(function (r) { if (r && r.ok) writeIfNeeded(data); }).catch(function () {});
        }
      } catch (e) { /* never break the caller's fetch */ }
      return res;
    };
  }

  function init() {
    var data = captureFirstTouch();
    if (!data) return;          // nothing to attribute
    installFetchHook(data);     // catch the first add-to-cart
    writeIfNeeded(data);        // covers a returning visitor who already has a cart
  }

  if (IS_BROWSER) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  // Node-only, for assets/glend-attribution.test.js. The browser has no
  // `module`, so this is inert on the storefront.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { parseParams: parseParams, referrerSource: referrerSource,
                       firstTouchFrom: firstTouchFrom, toAttributes: toAttributes };
  }
})();
